// ONBW1-S3 (docs/build-briefs/ONBW1-S3-owner-wizard.md; DEC-416; docs/design/onboarding-wizard.md). The pure rules behind the
// location owner's guided setup, kept out of setupWizard.ts so they can be tested without a browser.
//
// STEP STATE IS COMPUTED, NOT STORED. Wherever the 15-item checklist can say whether a step is done (address, business day, tax,
// bottles, recipes, starting stock, users, readers), that is what decides it, so the first-day banner and the Settings checklist
// keep working unchanged. The little the checklist cannot say is the one stored fact per location (GET and PUT .../setup-wizard):
// which steps were skipped, which were reviewed with Continue (tips, the bottle choice, the time zone, the test sale), when the
// owner chose Save and finish later, and when the wizard reached You are ready to sell.

import { READER_TIP_DEFAULTS } from "../../src/config/readerTipping.ts";
import { zoneOptionLabel } from "./timeZoneOptions.ts";
import type { ChecklistStates } from "./setupWizardModel.ts";

// ---------------------------------------------------------------------------------------------------
// Steps and chapters
// ---------------------------------------------------------------------------------------------------

export type OwnerStepId =
  | "address"
  | "timeZone"
  | "closing"
  | "tax"
  | "tips"
  | "bottles"
  | "firstDrink"
  | "stock"
  | "team"
  | "cardAccount"
  | "reader"
  | "testSale"
  | "royalty"
  | "connections";

export type OwnerExtraId = "cash" | "receipts" | "tipWhere" | "printer" | "vendors" | "device";

/** Mirrors src/domain/setupWizardProgress.ts WIZARD_STEP_IDS and WIZARD_EXTRA_IDS (plus the "discounts" id the server also
 * accepts); test/ui-owner-wizard-model.test.ts holds the lists equal. */
export const OWNER_STEP_IDS: readonly OwnerStepId[] = [
  "address",
  "timeZone",
  "closing",
  "tax",
  "tips",
  "bottles",
  "firstDrink",
  "stock",
  "team",
  "cardAccount",
  "reader",
  "testSale",
  "royalty",
  "connections",
];
export const OWNER_EXTRA_IDS: readonly OwnerExtraId[] = [
  "cash",
  "receipts",
  "tipWhere",
  "printer",
  "vendors",
  "device",
];

export type ChapterId = "bar" | "money" | "drinks" | "people" | "card" | "connections" | "ready";

export const CHAPTERS: ReadonlyArray<{ id: ChapterId; label: string }> = [
  { id: "bar", label: "Your location" },
  { id: "money", label: "Money" },
  { id: "drinks", label: "Drinks" },
  { id: "people", label: "People" },
  { id: "card", label: "Card payments" },
  { id: "connections", label: "Connections" },
  { id: "ready", label: "Ready" },
];

export interface OwnerStepDef {
  id: OwnerStepId;
  chapter: ChapterId;
  /** The question or title at the top of the screen. */
  title: string;
  /** What Welcome back and What is left call it. */
  summary: string;
  /** A rough time for Welcome back. */
  minutes: number;
  /** An optional step: marked Optional with a Skip, never a blocker, and never part of the required count (ACCTEXPORT1, DEC-514). */
  optional?: boolean;
}

const ALL_STEPS: readonly OwnerStepDef[] = [
  {
    id: "address",
    chapter: "bar",
    title: "Where is your location?",
    summary: "Address",
    minutes: 1,
  },
  {
    id: "timeZone",
    chapter: "bar",
    title: "Is this your time zone?",
    summary: "Time zone",
    minutes: 1,
  },
  {
    id: "closing",
    chapter: "bar",
    title: "When does your location day end?",
    summary: "Closing time",
    minutes: 1,
  },
  {
    id: "tax",
    chapter: "money",
    title: "What sales tax do you charge?",
    summary: "Sales tax",
    minutes: 2,
  },
  { id: "tips", chapter: "money", title: "What tips do guests see?", summary: "Tips", minutes: 1 },
  {
    id: "bottles",
    chapter: "drinks",
    title: "How do you want to add your bottles?",
    summary: "Bottles",
    minutes: 4,
  },
  {
    id: "firstDrink",
    chapter: "drinks",
    title: "Build your first drink",
    summary: "First drink",
    minutes: 3,
  },
  {
    id: "stock",
    chapter: "drinks",
    title: "How many bottles do you have?",
    summary: "Starting stock",
    minutes: 2,
  },
  {
    id: "team",
    chapter: "people",
    title: "Who works at your location?",
    summary: "Your team",
    minutes: 3,
  },
  {
    id: "cardAccount",
    chapter: "card",
    title: "We are setting up your card account",
    summary: "Card account",
    minutes: 2,
  },
  {
    id: "reader",
    chapter: "card",
    title: "Connect your card reader",
    summary: "Card reader",
    minutes: 3,
  },
  { id: "testSale", chapter: "card", title: "Try one sale", summary: "Test sale", minutes: 2 },
  {
    id: "royalty",
    chapter: "card",
    title: "Bank accounts",
    summary: "Bank accounts",
    minutes: 3,
  },
  {
    id: "connections",
    chapter: "connections",
    title: "Connect your other tools",
    summary: "Connections",
    minutes: 2,
    optional: true,
  },
];

export interface StepListOptions {
  /** The card payments account step is shown only while the deployment has connected accounts on. Dark: hidden. */
  cardAccountVisible: boolean;
  /** BANK1: the server offers a bank debit authorization (Royalties for a franchise location with a rate in force, or the Snip &
   * Sip subscription). The step keeps its saved-progress id "royalty" (PAY2-S6) but reads Bank accounts. */
  royaltyOffered: boolean;
  /** ACCTEXPORT1 and PAYEXPORT1: at least one outside connection (QuickBooks, Gusto) is on for this deployment. The optional
   * Connections step shows only then. */
  connectionsOffered?: boolean;
}

/** The steps this owner walks, in order. The card account step is hidden while the feature is dark, and Royalty payments
 * shows only when the server offers it. */
export function ownerSteps(options: StepListOptions): readonly OwnerStepDef[] {
  return ALL_STEPS.filter((step) => {
    if (step.id === "cardAccount") return options.cardAccountVisible;
    if (step.id === "royalty") return options.royaltyOffered;
    if (step.id === "connections") return options.connectionsOffered === true;
    return true;
  });
}

export function stepDef(id: OwnerStepId): OwnerStepDef {
  const found = ALL_STEPS.find((step) => step.id === id);
  if (found === undefined) throw new Error(`unknown wizard step ${id}`);
  return found;
}

// ---------------------------------------------------------------------------------------------------
// Stored progress and what the live reads say
// ---------------------------------------------------------------------------------------------------

export interface WizardProgress {
  skippedSteps: readonly string[];
  reviewedSteps: readonly string[];
  /** ISO instant, set by Save and finish later. */
  pausedAt: string | null;
  /** ISO instant, set once the wizard reached You are ready to sell. */
  finishedAt: string | null;
}

export const EMPTY_PROGRESS: WizardProgress = {
  skippedSteps: [],
  reviewedSteps: [],
  pausedAt: null,
  finishedAt: null,
};

/** What the card steps read. Undefined until the reads answer, so a step is never shown done on a guess. */
export interface OwnerCardReads {
  /** The deployment's connected accounts feature is on for this location. False is dark: the account step is hidden. */
  accountLive: boolean;
  /** Connected accounts are live and this location's account can take charges. */
  accountEnabled: boolean;
  readerRegistered: boolean;
  /** A test sale was approved and refunded in this session. The stored fact is the reviewed step "testSale". */
  testSaleApproved: boolean;
}

export interface OwnerReads {
  states: ChecklistStates;
  progress: WizardProgress;
  /** Street, city, state and ZIP all filled. Undefined until the settings answer arrives. */
  addressComplete: boolean | undefined;
  card: OwnerCardReads | undefined;
  /** PAY2-S6. */
  royalty: { offered: boolean; active: boolean } | undefined;
  /** ACCTEXPORT1 and PAYEXPORT1: whether a QuickBooks or Gusto connection is already made. Undefined until the reads answer. */
  connections?: { anyConnected: boolean } | undefined;
}

export type OwnerStepState = "done" | "skipped" | "pending";

/** done when the live reads (or the stored review) say so; skipped when the owner skipped it and it is still not done;
 * otherwise pending. */
export function ownerStepState(id: OwnerStepId, reads: OwnerReads): OwnerStepState {
  const reviewed = (step: string): boolean => reads.progress.reviewedSteps.includes(step);
  let done = false;
  switch (id) {
    case "address":
      done = reads.addressComplete === true;
      break;
    case "timeZone":
      done = reads.states["businessDay"] === "done" || reviewed("timeZone");
      break;
    case "closing":
      done = reads.states["businessDay"] === "done";
      break;
    case "tax":
      done = reads.states["taxRate"] === "done";
      break;
    case "tips":
      done = reviewed("tips");
      break;
    case "bottles":
      done = reads.states["bottles"] === "done" || reviewed("bottles");
      break;
    case "firstDrink":
      done = reads.states["recipes"] === "done";
      break;
    case "stock":
      done = reads.states["startingStock"] === "done";
      break;
    case "team":
      done = reads.states["users"] === "done" || reviewed("team");
      break;
    case "cardAccount":
      done = reads.card?.accountEnabled === true;
      break;
    case "reader":
      done = reads.card?.readerRegistered === true;
      break;
    case "testSale":
      done = reads.card?.testSaleApproved === true || reviewed("testSale");
      break;
    case "royalty":
      done = reads.royalty?.active === true;
      break;
    case "connections":
      done = reads.connections?.anyConnected === true || reviewed("connections");
      break;
  }
  if (done) return "done";
  return reads.progress.skippedSteps.includes(id) ? "skipped" : "pending";
}

export function allStepStates(steps: readonly OwnerStepDef[], reads: OwnerReads): OwnerStepState[] {
  return steps.map((step) => ownerStepState(step.id, reads));
}

/** The step the wizard opens on and Welcome back names: the first one still pending; when none is pending, the first one
 * the owner skipped; undefined when every step is done. */
export function firstUnfinishedIndex(states: readonly OwnerStepState[]): number | undefined {
  const pending = states.findIndex((state) => state === "pending");
  if (pending >= 0) return pending;
  const skipped = states.findIndex((state) => state === "skipped");
  return skipped >= 0 ? skipped : undefined;
}

/** Continue: the next step after this one that is not done. Undefined after the last, where the wizard shows Ready. */
export function nextIndex(current: number, states: readonly OwnerStepState[]): number | undefined {
  for (let i = current + 1; i < states.length; i += 1) {
    if (states[i] !== "done") return i;
  }
  return undefined;
}

/** Back is always one tap and always one step, across done steps too. Undefined on the first. */
export function previousIndex(current: number): number | undefined {
  return current > 0 ? current - 1 : undefined;
}

/** The chapter bar: done once every step of the chapter is done; the current chapter is the current step's. */
export type ChapterState = "done" | "current" | "pending";

export function chapterStates(
  steps: readonly OwnerStepDef[],
  states: readonly OwnerStepState[],
  current: { kind: "step"; index: number } | { kind: "ready" },
): Array<{ id: ChapterId; label: string; state: ChapterState }> {
  const currentChapter: ChapterId =
    current.kind === "ready" ? "ready" : (steps[current.index]?.chapter ?? "bar");
  return CHAPTERS.filter(
    (chapter) => chapter.id !== "connections" || steps.some((step) => step.chapter === "connections"),
  ).map((chapter) => {
    if (chapter.id === currentChapter) return { ...chapter, state: "current" as const };
    if (chapter.id === "ready") return { ...chapter, state: "pending" as const };
    const own = steps
      .map((step, i) => ({ step, state: states[i] }))
      .filter((entry) => entry.step.chapter === chapter.id);
    // A chapter with no steps in this list (the card chapter when everything in it is hidden) counts as done.
    const done = own.every(
      (entry) => entry.state === "done" || (entry.step.optional === true && entry.state === "skipped"),
    );
    // A chapter the owner is past, with something skipped in it, is not ticked: it is not done.
    return { ...chapter, state: done ? ("done" as const) : ("pending" as const) };
  });
}

/** "Step 4 of 12" for the top bar. An optional step says Optional instead and is never part of the total. */
export function stepLine(index: number, total: number, optional = false): string {
  return optional ? "Optional" : `Step ${index + 1} of ${total}`;
}

/** How many steps are required: the optional ones are never counted. */
export function requiredStepCount(steps: readonly OwnerStepDef[]): number {
  return steps.filter((step) => step.optional !== true).length;
}

// ---------------------------------------------------------------------------------------------------
// Ready: what the screen says, and what is left
// ---------------------------------------------------------------------------------------------------

/** What a location needs before it can take a card sale: where it is, when its day ends, a tax rate, a drink to sell and a reader.
 * Tips, bottles added by choice, starting stock, the team and the test sale can wait. The card account is needed only while
 * the deployment has connected accounts on. */
export function requiredStepIds(steps: readonly OwnerStepDef[]): OwnerStepId[] {
  const required: OwnerStepId[] = ["address", "timeZone", "closing", "tax", "firstDrink", "reader"];
  if (steps.some((step) => step.id === "cardAccount")) required.push("cardAccount");
  return required;
}

export interface ReadyView {
  ready: boolean;
  title: string;
  /** The first line under the title; one short sentence. */
  hint: string;
  /** The steps that keep this location from selling, by summary, when not ready. */
  blockers: string[];
}

export function readyView(
  steps: readonly OwnerStepDef[],
  states: readonly OwnerStepState[],
  testSaleDone: boolean,
): ReadyView {
  const required = requiredStepIds(steps);
  const blockers = steps
    .filter((step, i) => required.includes(step.id) && states[i] !== "done")
    .map((step) => step.summary);
  if (blockers.length === 0) {
    return {
      ready: true,
      title: "You are ready to sell",
      hint: testSaleDone ? "Your test sale worked." : "You can try a test sale any time.",
      blockers,
    };
  }
  return { ready: false, title: "Not ready to sell yet", hint: "These still need you.", blockers };
}

/** The Done list on the ready screen, one line per group of steps, ticked only when every step of the group is done. */
export interface ReadyLine {
  label: string;
  done: boolean;
}

const READY_GROUPS: ReadonlyArray<{ label: string; ids: readonly OwnerStepId[] }> = [
  { label: "Location address and time zone", ids: ["address", "timeZone", "closing"] },
  { label: "Sales tax and tips", ids: ["tax", "tips"] },
  { label: "First drink", ids: ["firstDrink"] },
  { label: "Your team", ids: ["team"] },
  { label: "Card payments", ids: ["cardAccount", "royalty"] },
  { label: "Card reader and test sale", ids: ["reader", "testSale"] },
];

export function readyLines(
  steps: readonly OwnerStepDef[],
  states: readonly OwnerStepState[],
): ReadyLine[] {
  const lines: ReadyLine[] = [];
  for (const group of READY_GROUPS) {
    const members = steps
      .map((step, i) => ({ step, state: states[i] }))
      .filter((entry) => group.ids.includes(entry.step.id));
    if (members.length === 0) continue;
    lines.push({ label: group.label, done: members.every((entry) => entry.state === "done") });
  }
  return lines;
}

// ---------------------------------------------------------------------------------------------------
// Welcome back and What is left
// ---------------------------------------------------------------------------------------------------

export interface WelcomeBackView {
  /** "You are 6 steps in". */
  title: string;
  /** The next step's own title, with its number. Undefined when nothing is left to do. */
  next: { number: number; title: string; minutes: number } | undefined;
  done: string[];
  todo: string[];
}

export function welcomeBackView(
  steps: readonly OwnerStepDef[],
  states: readonly OwnerStepState[],
): WelcomeBackView {
  // An optional step is never a thing left to do and never part of the count.
  const requiredOnly = states.map((state, i) => (steps[i]?.optional === true ? ("done" as const) : state));
  const doneCount = states.filter((state, i) => state === "done" && steps[i]?.optional !== true).length;
  const nextAt = firstUnfinishedIndex(requiredOnly);
  const nextStep = nextAt === undefined ? undefined : steps[nextAt];
  return {
    title: doneCount === 1 ? "You are 1 step in" : `You are ${doneCount} steps in`,
    next:
      nextAt === undefined || nextStep === undefined
        ? undefined
        : { number: nextAt + 1, title: nextStep.title, minutes: nextStep.minutes },
    done: steps.filter((step, i) => states[i] === "done" && step.optional !== true).map((step) => step.summary),
    todo: steps.filter((step, i) => states[i] !== "done" && step.optional !== true).map((step) => step.summary),
  };
}

export interface ExtraDef {
  id: OwnerExtraId;
  label: string;
  /** The button on What is left. */
  action: string;
  /** The screen's own title. */
  title: string;
}

export const EXTRAS: readonly ExtraDef[] = [
  {
    id: "printer",
    label: "Receipt printer",
    action: "Ask for help",
    title: "Add a receipt printer",
  },
  { id: "cash", label: "Cash drawer", action: "Set up", title: "Do you take cash?" },
  { id: "receipts", label: "Receipts", action: "Review", title: "How do guests get receipts?" },
  { id: "tipWhere", label: "Where guests tip", action: "Review", title: "Where do guests tip?" },
  {
    id: "vendors",
    label: "Vendors and order days",
    action: "Set up",
    title: "Who do you buy from?",
  },
  { id: "device", label: "Another device", action: "Make code", title: "Add another device" },
];

export type ExtraState = "done" | "asked" | "pending";

export function extraState(id: OwnerExtraId, reads: OwnerReads): ExtraState {
  const reviewed = reads.progress.reviewedSteps.includes(id);
  switch (id) {
    case "vendors":
      return reads.states["vendors"] === "done" ? "done" : "pending";
    case "printer":
      return reviewed ? "asked" : "pending";
    case "device":
      // A code is made on demand; making one does not finish anything, so it is never done.
      return "pending";
    default:
      return reviewed ? "done" : "pending";
  }
}

export interface WhatsLeftView {
  canWait: Array<{ id: OwnerExtraId; label: string; action: string; state: ExtraState }>;
  skipped: Array<{ id: OwnerStepId; number: number; label: string }>;
  doneCount: number;
  totalSteps: number;
}

export function whatsLeftView(
  steps: readonly OwnerStepDef[],
  states: readonly OwnerStepState[],
  reads: OwnerReads,
): WhatsLeftView {
  return {
    canWait: EXTRAS.filter((extra) => extraState(extra.id, reads) !== "done").map((extra) => ({
      id: extra.id,
      label: extra.label,
      action: extra.action,
      state: extraState(extra.id, reads),
    })),
    skipped: steps
      .map((step, i) => ({ step, state: states[i], number: i + 1 }))
      .filter(
        (entry) =>
          entry.state === "skipped" || (entry.state === "pending" && entry.step.optional !== true),
      )
      .map((entry) => ({ id: entry.step.id, number: entry.number, label: entry.step.summary })),
    doneCount: states.filter((state, i) => state === "done" && steps[i]?.optional !== true).length,
    totalSteps: requiredStepCount(steps),
  };
}

/** The sign-in rule: Welcome back opens after a sign-in only when the owner chose Save and finish later. Anything else
 * opens nothing new (the first-day banner reopens the wizard). */
export function opensWelcomeBack(progress: WizardProgress): boolean {
  return progress.pausedAt !== null;
}

// ---------------------------------------------------------------------------------------------------
// Where is your location: the address
// ---------------------------------------------------------------------------------------------------

export interface AddressDraft {
  street: string;
  city: string;
  state: string;
  postalCode: string;
}

export const ADDRESS_NEEDS = "Fill in all four";

export function addressIsComplete(draft: AddressDraft): boolean {
  return (
    draft.street.trim().length > 0 &&
    draft.city.trim().length > 0 &&
    draft.state.length > 0 &&
    /^\d{5}(-\d{4})?$/.test(draft.postalCode.trim())
  );
}

/** The one reason Continue is dimmed on the address screen; "" when it is live. */
export function addressBlockedReason(draft: AddressDraft): string {
  const anyStarted =
    draft.street.trim() !== "" ||
    draft.city.trim() !== "" ||
    draft.state !== "" ||
    draft.postalCode.trim() !== "";
  if (addressIsComplete(draft)) return "";
  if (!anyStarted) return "";
  if (
    draft.street.trim() !== "" &&
    draft.city.trim() !== "" &&
    draft.state !== "" &&
    draft.postalCode.trim() !== ""
  ) {
    return "Enter a 5 digit ZIP";
  }
  return ADDRESS_NEEDS;
}

// ---------------------------------------------------------------------------------------------------
// Time zone and closing time
// ---------------------------------------------------------------------------------------------------

/** The zones a location picks from when the suggestion is wrong. "Eastern Time" is how a person says it. */
export const ZONE_CHOICES: ReadonlyArray<{ timeZone: string; label: string }> = [
  { timeZone: "America/New_York", label: "Eastern Time" },
  { timeZone: "America/Chicago", label: "Central Time" },
  { timeZone: "America/Denver", label: "Mountain Time" },
  { timeZone: "America/Phoenix", label: "Arizona" },
  { timeZone: "America/Los_Angeles", label: "Pacific Time" },
  { timeZone: "America/Anchorage", label: "Alaska Time" },
  { timeZone: "Pacific/Honolulu", label: "Hawaii Time" },
];

/** UX133: "UTC-05:00 Eastern Time (New York)", the same label every time zone choice in the product shows. */
export function zoneLabel(timeZone: string): string {
  return zoneOptionLabel(timeZone);
}

/** What the server's time zone suggestion says, in the wizard's terms. */
export type ZoneSuggestion = { kind: "suggested"; timeZone: string } | { kind: "none" };

export function zoneSuggestionOf(input: unknown): ZoneSuggestion {
  if (typeof input === "object" && input !== null) {
    const record = input as Record<string, unknown>;
    if (
      record["available"] === true &&
      typeof record["timeZone"] === "string" &&
      ZONE_CHOICES.some((choice) => choice.timeZone === record["timeZone"])
    ) {
      return { kind: "suggested", timeZone: record["timeZone"] };
    }
  }
  return { kind: "none" };
}

/** "Charlotte, North Carolina" from the address, or "" when the city or state is missing. */
export function placeLabel(city: string, stateName: string): string {
  const parts = [city.trim(), stateName.trim()].filter((part) => part.length > 0);
  return parts.join(", ");
}

/** The closing time buttons: midnight to 5 AM. */
export const CLOSING_CHOICES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "00:00", label: "Midnight" },
  { value: "01:00", label: "1 AM" },
  { value: "02:00", label: "2 AM" },
  { value: "03:00", label: "3 AM" },
  { value: "04:00", label: "4 AM" },
  { value: "05:00", label: "5 AM" },
];
export const CLOSING_DEFAULT = "03:00";

function closingHourLabel(hour: number): string {
  return hour === 0 ? "Midnight" : hour < 12 ? `${hour} AM` : hour === 12 ? "Noon" : `${hour - 12} PM`;
}

/** DAYEND1: the "Pick another time" list, shared by the setup wizard and location Settings. Evening
 * times first (5 PM to 11 PM), Midnight, 1 AM to 11 AM, then Noon to 4 PM. Every whole hour once. */
export const CLOSING_HOUR_ORDER: ReadonlyArray<number> = [
  17, 18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
];
export const CLOSING_HOUR_CHOICES: ReadonlyArray<{ value: string; label: string }> =
  CLOSING_HOUR_ORDER.map((hour) => ({
    value: `${String(hour).padStart(2, "0")}:00`,
    label: closingHourLabel(hour),
  }));
export const CLOSING_HOUR_HINT =
  "Close in the evening? Pick your closing time. Open past midnight? Pick when you close.";

// ---------------------------------------------------------------------------------------------------
// Money entry: a number pad that fills from the right, so two decimals are always there
// ---------------------------------------------------------------------------------------------------

/** Typing 4, 7, 5 reads 0.04, 0.47, 4.75. `max` is the ceiling in the same units (cents, or hundredths of a percent). */
export function padAppend(value: number, digit: string, max: number): number {
  if (!/^\d$/.test(digit)) return value;
  const next = value * 10 + Number(digit);
  return next > max ? value : next;
}

export function padDelete(value: number): number {
  return Math.floor(value / 10);
}

/** "4.75" from 475. */
export function twoDecimals(units: number): string {
  const whole = Math.floor(units / 100);
  const fraction = String(units % 100).padStart(2, "0");
  return `${whole}.${fraction}`;
}

/** "4.75%" from basis points. Always two decimals. */
export function percentText(basisPoints: number): string {
  return `${twoDecimals(basisPoints)}%`;
}

/** "$6.25" from cents. */
export function dollarText(cents: number): string {
  return `$${twoDecimals(cents)}`;
}

// ---------------------------------------------------------------------------------------------------
// Sales tax
// ---------------------------------------------------------------------------------------------------

export type TaxPartId = "state" | "county" | "city" | "special_district";

export const TAX_PARTS: ReadonlyArray<{ id: TaxPartId; label: string; optional: boolean }> = [
  { id: "state", label: "State", optional: false },
  { id: "county", label: "County", optional: false },
  { id: "city", label: "City", optional: true },
  { id: "special_district", label: "Special district or transit", optional: true },
];

/** The ceiling of one part, in basis points: the server's own cap on a recorded rate. */
export const MAX_TAX_BASIS_POINTS = 2500;
/** Each tap on a stepper moves a part by a quarter of a percent. */
export const TAX_STEP_BASIS_POINTS = 25;

export interface TaxDraft {
  mode: "parts" | "single";
  /** null is a part that is not used. */
  parts: Record<TaxPartId, number | null>;
  /** The one combined rate, in basis points, or null until entered. */
  single: number | null;
  /** The owner changed the figures after the suggestion; the screen then says the suggestion was edited. */
  edited: boolean;
}

export interface TaxSuggestionView {
  available: boolean;
  parts: Record<TaxPartId, number>;
  /** YYYY-MM-DD, or null. */
  asOf: string | null;
}

/** The suggestion in the wizard's terms. Anything the service could not answer is not available: the screen opens on the one
 * combined rate with nothing in it, never a guessed number. */
export function taxSuggestionOf(input: unknown): TaxSuggestionView {
  const none: TaxSuggestionView = {
    available: false,
    parts: { state: 0, county: 0, city: 0, special_district: 0 },
    asOf: null,
  };
  if (typeof input !== "object" || input === null) return none;
  const tax = input as Record<string, unknown>;
  if (tax["available"] !== true || !Array.isArray(tax["components"])) return none;
  const parts: Record<TaxPartId, number> = { state: 0, county: 0, city: 0, special_district: 0 };
  for (const entry of tax["components"] as unknown[]) {
    if (typeof entry !== "object" || entry === null) return none;
    const record = entry as Record<string, unknown>;
    const id = record["component"];
    const basisPoints = record["basisPoints"];
    if (
      (id !== "state" && id !== "county" && id !== "city" && id !== "special_district") ||
      typeof basisPoints !== "number" ||
      !Number.isInteger(basisPoints) ||
      basisPoints < 0
    ) {
      return none;
    }
    parts[id] = basisPoints;
  }
  const asOf = typeof tax["asOf"] === "string" ? tax["asOf"] : null;
  return { available: true, parts, asOf };
}

/** The draft the screen opens on: the suggestion's parts, with a zero city or special district as Not used; or the single
 * rate with nothing in it when there is no suggestion. */
export function taxDraftFromSuggestion(suggestion: TaxSuggestionView): TaxDraft {
  if (!suggestion.available) {
    return {
      mode: "single",
      parts: { state: null, county: null, city: null, special_district: null },
      single: null,
      edited: false,
    };
  }
  return {
    mode: "parts",
    parts: {
      state: suggestion.parts.state,
      county: suggestion.parts.county,
      city: suggestion.parts.city > 0 ? suggestion.parts.city : null,
      special_district:
        suggestion.parts.special_district > 0 ? suggestion.parts.special_district : null,
    },
    single: null,
    edited: false,
  };
}

export function taxTotal(draft: TaxDraft): number {
  if (draft.mode === "single") return draft.single ?? 0;
  return TAX_PARTS.reduce((sum, part) => sum + (draft.parts[part.id] ?? 0), 0);
}

/** "4.75 + 2.00 + 0.50": the used parts in order, for the line under the total. */
export function taxSumLine(draft: TaxDraft): string {
  if (draft.mode === "single") return "";
  return TAX_PARTS.map((part) => draft.parts[part.id])
    .filter((value): value is number => value !== null && value > 0)
    .map((value) => twoDecimals(value))
    .join(" + ");
}

/** The tag beside the total: a suggestion until the owner confirms, and Edited once the figures were changed. */
export function taxTag(input: {
  suggestionAvailable: boolean;
  edited: boolean;
  confirmed: boolean;
  /** The screen opened on a rate already saved for the location. */
  saved?: boolean;
}): string {
  if (input.confirmed) return "Confirmed";
  if (input.saved === true && !input.edited) return "Saved, not confirmed";
  if (!input.suggestionAvailable) return "Not confirmed";
  return input.edited ? "Changed by you, not confirmed" : "Suggestion, not confirmed";
}

/** The one reason Continue is dimmed on the tax screen. */
export const TAX_NEEDS_CONFIRM = "Confirm the rate to continue";
export const TAX_NEEDS_RATE = "Enter the rate";

export function taxBlockedReason(draft: TaxDraft, confirmed: boolean): string {
  if (confirmed) return "";
  if (draft.mode === "single" && draft.single === null) return TAX_NEEDS_RATE;
  return TAX_NEEDS_CONFIRM;
}

/** What Use 7.25% sends. Named parts for the breakdown (zero parts left out, one zero state part when the whole rate is
 * zero, since a rate set must name something); the one combined rate as a single unsplit part. */
export function taxComponentsToRecord(
  draft: TaxDraft,
): Array<{ component: string; basisPoints: number }> {
  if (draft.mode === "single") {
    return [{ component: "unsplit", basisPoints: draft.single ?? 0 }];
  }
  const used = TAX_PARTS.map((part) => ({
    component: part.id as string,
    basisPoints: draft.parts[part.id] ?? 0,
  })).filter((entry) => entry.basisPoints > 0);
  return used.length > 0 ? used : [{ component: "state", basisPoints: 0 }];
}

export function stepTaxPart(draft: TaxDraft, part: TaxPartId, direction: 1 | -1): TaxDraft {
  const current = draft.parts[part] ?? 0;
  const next = Math.min(
    MAX_TAX_BASIS_POINTS,
    Math.max(0, current + direction * TAX_STEP_BASIS_POINTS),
  );
  return { ...draft, parts: { ...draft.parts, [part]: next }, edited: true };
}

export function setTaxPart(draft: TaxDraft, part: TaxPartId, basisPoints: number | null): TaxDraft {
  return { ...draft, parts: { ...draft.parts, [part]: basisPoints }, edited: true };
}

/** The label on the confirm tap: "Use 7.25%". */
export function useRateLabel(draft: TaxDraft): string {
  return `Use ${percentText(taxTotal(draft))}`;
}

// ---------------------------------------------------------------------------------------------------
// Tips
// ---------------------------------------------------------------------------------------------------

/** What guests see on the card reader unless the owner changes them (DEC-416, ONBW1-S3 brief). Cash never tips.
 * TIPDEF1: taken from READER_TIP_DEFAULTS (20 / 22 / 25%), the one source the reader and the ticket default share. */
export const DEFAULT_TIP_BASIS_POINTS: readonly number[] = READER_TIP_DEFAULTS.percentages.map((p) => p * 100);

export const MAX_TIP_BASIS_POINTS = 10000;

export function tipText(basisPoints: number): string {
  return basisPoints % 100 === 0 ? `${basisPoints / 100}%` : percentText(basisPoints);
}

/** Three tip choices in rising order, each above zero. "" when they are good. */
export function tipsBlockedReason(values: readonly number[]): string {
  if (values.length !== 3) return "Pick three tips";
  if (
    values.some((value) => !Number.isInteger(value) || value <= 0 || value > MAX_TIP_BASIS_POINTS)
  ) {
    return "Each tip must be above 0%";
  }
  if (values[0] !== undefined && values[1] !== undefined && values[2] !== undefined) {
    if (!(values[0] < values[1] && values[1] < values[2])) return "Put the tips in rising order";
  }
  return "";
}

// ---------------------------------------------------------------------------------------------------
// Bottles and the first drink
// ---------------------------------------------------------------------------------------------------

export type BottleWay = "upload" | "hand" | "later";

/** The bottle sizes by hand: the label and the size the item records. */
export const BOTTLE_SIZES: ReadonlyArray<{
  value: string;
  label: string;
  sizeValue: number;
  sizeUnit: "ml" | "l";
}> = [
  { value: "750ml", label: "750 ml", sizeValue: 750, sizeUnit: "ml" },
  { value: "1l", label: "1 L", sizeValue: 1, sizeUnit: "l" },
  { value: "1.75l", label: "1.75 L", sizeValue: 1.75, sizeUnit: "l" },
];

export interface BottleDraft {
  name: string;
  /** A liquor category value, or "" while none is chosen. */
  category: string;
  /** The size value of BOTTLE_SIZES, or "" while none is chosen. */
  size: string;
  /** What the location charges per ounce, in cents; null until entered. */
  perOunceCents?: number | null;
  shelfTier?: string;
}

export function bottleBlockedReason(draft: BottleDraft): string {
  if (draft.name.trim().length === 0) return "Enter the bottle name";
  if (draft.category === "") return "Choose a type";
  if (draft.size === "") return "Choose a size";
  return "";
}

export const MAX_PRICE_CENTS = 100_000;

/** The pour sizes of the drink, in ounces. */
export const DRINK_POUR_CHOICES: readonly number[] = [1, 1.5, 2];

export function ouncesText(ounces: number): string {
  return `${ounces} oz`;
}

/** The drinks to start from. A suggestion names the drink and the kind of bottle it takes; Other names its own. */
export const DRINK_STARTERS: ReadonlyArray<{ id: string; name: string; category: string | null }> =
  [
    { id: "margarita", name: "Margarita", category: "tequila" },
    { id: "old_fashioned", name: "Old Fashioned", category: "bourbon" },
    { id: "vodka_soda", name: "Vodka Soda", category: "vodka" },
    { id: "other", name: "Other", category: null },
  ];

export interface DrinkDraft {
  starterId: string;
  /** The typed name, only for Other. */
  otherName: string;
  /** The chosen bottle's id, or "" while none is chosen. */
  liquorProductId: string;
  ounces: number | null;
  /** The extra charge, in cents. */
  buildChargeCents: number;
}

export function drinkName(draft: Pick<DrinkDraft, "starterId" | "otherName">): string {
  const starter = DRINK_STARTERS.find((entry) => entry.id === draft.starterId);
  if (starter === undefined) return "";
  return starter.id === "other" ? draft.otherName.trim() : starter.name;
}

export function drinkBlockedReason(draft: DrinkDraft): string {
  if (draft.starterId === "") return "Choose a drink";
  if (drinkName(draft) === "") return "Enter the drink name";
  if (draft.liquorProductId === "") return "Choose a bottle";
  if (draft.ounces === null) return "Choose how much you pour";
  return "";
}

/** The pour in cents, one rounding, half up, exactly as the server prices a line (ounces to hundredths of an ounce, times the
 * per ounce cents, divided by 100). */
export function pourCents(ounces: number, perOunceCents: number): number {
  const hundredths = Math.round(ounces * 100);
  const raw = hundredths * perOunceCents;
  return Math.floor((raw + 50) / 100);
}

export interface DrinkPriceView {
  /** "Tequila 2 oz x $1.40". */
  pourLine: string;
  pourCents: number;
  buildChargeCents: number;
  totalCents: number;
}

/** The price a guest pays, live: the pour (ounces times the bottle's price per ounce) plus the build charge, with one
 * rounding of the sum. Undefined until both a bottle and an amount are chosen. */
export function drinkPriceView(input: {
  bottleName: string | undefined;
  perOunceCents: number | undefined;
  draft: Pick<DrinkDraft, "ounces" | "buildChargeCents">;
}): DrinkPriceView | undefined {
  if (input.perOunceCents === undefined || input.draft.ounces === null) return undefined;
  const hundredths = Math.round(input.draft.ounces * 100);
  const raw = hundredths * input.perOunceCents + input.draft.buildChargeCents * 100;
  const total = Math.floor((raw + 50) / 100);
  return {
    pourLine: `${input.bottleName ?? "Bottle"} ${ouncesText(input.draft.ounces)} x ${dollarText(input.perOunceCents)}`,
    // The lines always add up to the total: the pour is what is left after the build charge.
    pourCents: total - input.draft.buildChargeCents,
    buildChargeCents: input.draft.buildChargeCents,
    totalCents: total,
  };
}

/** The liquor category a drink is filed under: the bottle's own when the menu has it, else Other. */
export function drinkCategoryFor(
  bottleCategory: string,
  menuCategories: readonly string[],
): string {
  return menuCategories.includes(bottleCategory) ? bottleCategory : "other";
}

// ---------------------------------------------------------------------------------------------------
// Starting stock
// ---------------------------------------------------------------------------------------------------

export const MAX_BOTTLES = 999;

export function stepBottleCount(count: number, direction: 1 | -1): number {
  return Math.min(MAX_BOTTLES, Math.max(0, count + direction));
}

// ---------------------------------------------------------------------------------------------------
// Card reader
// ---------------------------------------------------------------------------------------------------

/** The three words the reader shows, as the registration code: lowercase, joined with dashes. "" until all three are typed. */
export function readerCodeFromWords(words: readonly string[]): string {
  const cleaned = words.map((word) =>
    word
      .trim()
      .toLowerCase()
      .replace(/[^a-z]/g, ""),
  );
  return cleaned.length === 3 && cleaned.every((word) => word.length > 0) ? cleaned.join("-") : "";
}

export const READER_NEEDS_WORDS = "Type all 3 words";

/** The name the first reader gets. The owner is never asked to name it. */
export const FIRST_READER_NAME = "Front bar";

// ---------------------------------------------------------------------------------------------------
// Extras
// ---------------------------------------------------------------------------------------------------

export const RECEIPT_CHOICES: ReadonlyArray<{
  mode: "printed_and_email" | "email_only" | "ask";
  label: string;
}> = [
  { mode: "ask", label: "Ask each guest" },
  { mode: "printed_and_email", label: "Printed and email" },
  { mode: "email_only", label: "Email only" },
];

export const TIP_WHERE_CHOICES: ReadonlyArray<{ mode: "reader" | "receipt"; label: string }> = [
  { mode: "reader", label: "On the card reader" },
  { mode: "receipt", label: "On the paper receipt" },
];

/** Order every: the vendor's order pace, in days. */
export const ORDER_PACE_CHOICES: ReadonlyArray<{ days: number; label: string }> = [
  { days: 7, label: "Week" },
  { days: 14, label: "2 weeks" },
  { days: 30, label: "Month" },
];

/** Arrives in: the vendor's lead time, in days. */
export const LEAD_DAY_CHOICES: ReadonlyArray<{ days: number; label: string }> = [
  { days: 2, label: "2 days" },
  { days: 3, label: "3 days" },
  { days: 5, label: "5 days" },
];

export const MAX_DRAWER_CENTS = 1_000_000;

// ---------------------------------------------------------------------------------------------------
// The shape of the people list
// ---------------------------------------------------------------------------------------------------

/** "DW" from "Dana Whitfield". */
export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const first = words[0]?.charAt(0) ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? "") : "";
  return `${first}${last}`.toUpperCase();
}
